<template>
    <div v-tooltip="help" class="position-relative">
        <!-- Map mode toggle -->
        <div v-if="mappable && !isRpc" class="custom-control custom-switch map-switch">
            <input :value="isMap"
                   :checked="isMap"
                   @change="toggleMapMode"
                   type="checkbox"
                   :disabled="disabled"
                   :class="{disabled, clickable: !disabled}"
                   class="custom-control-input"
                   :id="id">
            <label class="custom-control-label" :for="id">
                map
            </label>
        </div>

        <AppMapParam
            v-if="isMap"
            v-bind="$props"
            :prop="prop"
            :model="model"
            :block="block"
            :disabled="disabled"
            :value="paramValue"
            :objectRefOnly="objectRefOnly"
            @change="onValueChange"/>

        <!-- App param -->
        <template v-else>
            <div v-if="isRpc"
                 v-tooltip="'Unable to run RPC ' + (_.isPlainObject(model) ? model.name : model)"
                 class="mt-2 text-danger">
                Render error
            </div>

            <component
                v-else-if="checkIsValidPram(model)"
                v-bind="{...$props, ...model}"
                :value="paramValue"
                :is="PREFIX + model.type"
                @change="onValueChange">

                <!-- NESTED, SPEC (Complex param recursive render) -->
                <template #item="{ props }">
                    <AppParam v-bind="{...$props, ...props, id: id + props.prop}"/>
                </template>
            </component>

            <div v-else class="my-2 text-danger">
                Unknown parameter type
            </div>
        </template>
    </div>
</template>

<script>
import {checkIsExistingParamType} from '../../models/apps/app-parameters'
import AppMapParam from './map-param/AppMapParam'
import {checkIsRpc} from '../../views/apps/rpc'
import {getRandomId} from '../../utils'
import {zParameterTypes} from '../../types/z/parameters'

const PREFIX = 'parameter_' // prefix to avoid HTML tags collision (like 'text', 'time')

export default {
    name: 'AppParam',
    inheritAttrs: false,
    props: {
        id: {
            type: String,
            required: false,
            default: getRandomId.bind(null, 'param')
        },
        block: {
            // type: Object,
            required: false
        },
        model: {
            // type: Object,
            // required: true
        },
        prop: { // String - prop, Array - path in value object for complex structures, undefined - failed to run rpc
            // type: [String, Array],
            required: true
        },
        value: {}, // root value object
        onChange: {
            type: Function,
            required: true
        },
        mappable: {
            type: Boolean,
            required: false,
            default: true
        },
        objectRefOnly: { // for datastore operation => disable typing + remove {{ }}
            type: Boolean,
            required: false,
            default: false
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        autofocus: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            PREFIX
        }
    },
    components: {
        AppMapParam,
        [PREFIX + zParameterTypes.TEXT]: () => import(/* webpackChunkName: "app_param_text" */ './AppParamText'),
        [PREFIX + zParameterTypes.EMAIL]: () => import(/* webpackChunkName: "app_param_email" */ './AppParamEmail'),
        [PREFIX + zParameterTypes.URL]: () => import(/* webpackChunkName: "app_param_url" */ './AppParamUrl'),
        [PREFIX + zParameterTypes.UUID]: () => import(/* webpackChunkName: "app_param_uuid" */ './AppParamUUID'),
        [PREFIX + zParameterTypes.COLLECTION]: () => import(/* webpackChunkName: "app_param_collection" */ './AppParamCollection'),
        [PREFIX + zParameterTypes.NUMBER]: () => import(/* webpackChunkName: "app_param_number" */ './AppParamNumber'),
        [PREFIX + zParameterTypes.INTEGER]: () => import(/* webpackChunkName: "app_param_number" */ './AppParamNumber'),
        [PREFIX + zParameterTypes.PORT]: () => import(/* webpackChunkName: "app_param_port" */ './AppParamPort'),
        [PREFIX + zParameterTypes.UINTEGER]: () => import(/* webpackChunkName: "app_param_number" */ './AppParamNumber'),
        [PREFIX + zParameterTypes.BOOLEAN]: () => import(/* webpackChunkName: "app_param_boolean" */ './AppParamBoolean'),
        [PREFIX + zParameterTypes.SELECT]: () => import(/* webpackChunkName: "app_param_select" */ './AppParamSelect'),
        [PREFIX + zParameterTypes.COLOR]: () => import(/* webpackChunkName: "app_param_color" */ './AppParamColor'),
        [PREFIX + zParameterTypes.JSON]: () => import(/* webpackChunkName: "app_param_json" */ './AppParamJson'),
        [PREFIX + zParameterTypes.DATE]: () => import(/* webpackChunkName: "app_param_date" */ './AppParamDate'),
        [PREFIX + zParameterTypes.TIME]: () => import(/* webpackChunkName: "app_param_time" */ './AppParamTime'),
        [PREFIX + zParameterTypes.TIMEZONE]: () => import(/* webpackChunkName: "app_param_timezone" */ './AppParamTimezone'),
        [PREFIX + zParameterTypes.TIMESTAMP]: () => import(/* webpackChunkName: "app_param_timestamp" */ './AppParamTimestamp'),
        [PREFIX + zParameterTypes.ARRAY]: () => import(/* webpackChunkName: "app_param_array" */ './AppParamArray'),
        [PREFIX + zParameterTypes.FILE]: () => import(/* webpackChunkName: "app_param_file" */ './AppParamFile'),
        [PREFIX + zParameterTypes.FOLDER]: () => import(/* webpackChunkName: "app_param_file" */ './AppParamFile'),
        [PREFIX + zParameterTypes.FILTER]: () => import(/* webpackChunkName: "app_param_filter" */ './AppParamFilter'),
        [PREFIX + zParameterTypes.FILENAME]: () => import(/* webpackChunkName: "app_param_filename" */ './AppParamFileName'),
        [PREFIX + zParameterTypes.BUFFER]: () => import(/* webpackChunkName: "app_param_buffer" */ './AppParamBuffer'),
        [PREFIX + zParameterTypes.PATH]: () => import(/* webpackChunkName: "app_param_path" */ './AppParamPath'),
        [PREFIX + zParameterTypes.CERT]: () => import(/* webpackChunkName: "app_param_cert" */ './AppParamCert'),
        [PREFIX + zParameterTypes.PKEY]: () => import(/* webpackChunkName: "app_param_cert" */ './AppParamCert'),
        [PREFIX + zParameterTypes.PASSWORD]: () => import(/* webpackChunkName: "app_param_password" */ './AppParamPassword')
    },
    computed: {
        isRpc() {
            return checkIsRpc(this.model)
        },

        isMap() {
            return _.get(this.value, this.prop, {}).isMap
        },

        paramValue() {
            return _.get(this.value, this.prop, {}).value
        },

        help() {
            return {
                html: true,
                content: this.model.help,
                placement: 'bottom-start',
                classes: 'tooltip-white',
                delay: 1000
            }
        }
    },
    methods: {
        checkIsValidPram(model) {
            if (!model || !model.type) {
                return false
            }
            return checkIsExistingParamType(model.type)
        },

        onValueChange({prop, value}) {
            if (!prop) { // if root field child may not send prop
                prop = this.prop
            }
            this.onChange({prop, data: {isMap: this.isMap, value}})
        },

        toggleMapMode() {
            this.onChange({prop: this.prop, data: {isMap: !this.isMap}})
        }
    },
    mounted() {
        if (this.autofocus) {
            const input = this.$el.querySelector('input')
            if (input) {
                this.$nextTick(() => {
                    input.focus()
                })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
    .map-switch {
        position: absolute;
        right: 0;
        top: -4px;

        label {
            line-height: 24px;
            vertical-align: middle;
            color: $color-grey-primary;

            &:hover {
                color: $color-purple-light;
                cursor: pointer;
            }
        }
    }
</style>
